import { useTimeStore } from './time'
import { useCookieStore } from './cookie'
import { defineStore } from 'pinia'

/**
 * @typedef Sale
 * @property {Date} start sale start date
 * @property {Date} end sale end date
 * @property {string} name sale name
 * @property {string} slug sale slug
 * @property {string?} cookie sale banner cookie key
 * @property {string?} href sale link target. Defaults to /learning/sale/{slug}
 * @property {string?} utmLabel value of utm_campaign UTM tag. Defaults to sale slug
 * @property {string?} cssClass CSS class to be added to the banner and menu items. Defaults to sale slug
 * @property {string?} menuLabel sale menu button label
 */

export const useSaleStore = defineStore({
    id: 'sale',

    /**
     * @returns {{
     *     sales: Sale[],
     * }}
     */
    state () {
        return {
            sales: [
                {
                    start:     new Date('2025-04-15T21:00:00.000Z'),
                    end:       new Date('2025-04-28T21:00:00.000Z'),
                    name:      'Весняний розпродаж',
                    slug:      'spring-2025',
                    menuLabel: 'Акція до -60%',
                },

                {
                    // start:     new Date('2025-02-27T22:00:00.000Z'),
                    start:     new Date('2025-03-03T22:00:00.000Z'),
                    end:       new Date('2025-03-14T22:00:00.000Z'),
                    name:      'Знижки на передплату',
                    cookie:    'subscription-discount-2025',
                    slug:      'subscription-discount',
                    href:      '/learning/subscription',
                    menuLabel: 'Знижки -50%',
                },

                // Past sales left in for reference
                /*
                {
                    start:     new Date('2024-12-01T22:00:00.000Z'),
                    end:       new Date('2024-12-02T21:59:59.000Z'),
                    name:      'Кіберпонеділок',
                    slug:      'cyber-monday-2024',
                    menuLabel: 'Акція до -70%',
                },
                {
                    start:     new Date('2024-11-21T22:00:00.000Z'),
                    end:       new Date('2024-11-29T21:59:59.000Z'),
                    name:      'Чорна п’ятниця',
                    slug:      'black-friday-2024',
                    menuLabel: 'Акція до -90%',
                },
                */
            ],
        }
    },

    getters: {
        /** @returns {Sale|false} */
        activeSale () {
            const cookie = useCookieStore();

            if (!cookie.isUkrainian) return false;

            const time = useTimeStore();

            for (const sale of this.sales) {
                if (sale.start <= time.now && time.now <= sale.end) return sale;
            }

            return false;
        },

        /** @returns {Date|false} */
        start () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && sale.start;
        },

        /** @returns {Date|false} */
        end () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && sale.end;
        },

        /** @returns {string|false} */
        href () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            if (!sale) return false;

            return sale.href || `/learning/sale/${sale.slug}`;
        },

        /** @returns {string|false} */
        slug () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && sale.slug;
        },

        /** @returns {string|false} */
        cookie () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && (sale.cookie || sale.slug);
        },

        /** @returns {string|false} */
        utmLabel () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && (sale.utmLabel || sale.slug);
        },

        /** @returns {string|false} */
        cssClass () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && (sale.class || sale.slug);
        },

        /** @returns {string|false} */
        name () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && sale.name;
        },

        /** @returns {string|false} */
        menuLabel () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return sale && sale.menuLabel;
        },

        /** @returns {boolean} */
        isSaleActive () {
            /** @type {Sale|false} */
            const sale = this.activeSale;

            return !!sale;
        },
    }
})